import type { CacheConfig, RequestParameters, Variables } from 'relay-runtime';

import { expValEquals } from '@confluence/feature-experiments';

const AGG_BASE_URI = '/gateway/api/graphql';
export const AGG_SLOW_URI = `${AGG_BASE_URI}/slow`;

/**
 * Returns true when running in SSR and batch/SLOW URI should be used.
 *
 * By default, SSR requests use the batch endpoint with the SLOW URI.
 * When the experiment `confluence_ssr_relay_persisted_query_endpoint` is enabled,
 * SSR requests switches to using persisted query (/pq) endpoints instead.
 *
 */
let _ssrBatchEndpointCached: boolean | undefined;
export const shouldUseSSRBatchEndpoint = (): boolean => {
	if (_ssrBatchEndpointCached !== undefined) {
		return _ssrBatchEndpointCached;
	}

	_ssrBatchEndpointCached =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!!process.env?.REACT_SSR &&
		!expValEquals('confluence_ssr_relay_persisted_query_endpoint', 'isEnabled', true);
	return _ssrBatchEndpointCached;
};

// Exported only for testing — allows resetting the memoised value between tests.
export const __resetSSRBatchEndpointCache = (): void => {
	_ssrBatchEndpointCached = undefined;
};

/**
 * Returns the AGG endpoint URL for a given request.
 *
 * During SSR (default, without the persisted query experiment):
 * - Uses `/gateway/api/graphql/slow` without persisted query (PQ) paths.
 *   The caller is responsible for appending `/batch` when sending multiple requests
 *   (see `getBatchEndpoint`).
 *
 * During SSR with the `confluence_ssr_relay_persisted_query_endpoint` experiment enabled,
 * or on the client side:
 * - Uses the persisted query endpoint `/gateway/api/graphql/pq/{id}` when request.id is available
 * - Respects META_SLOW_ENDPOINT metadata for slow endpoint routing
 */
export const getAggEndpoint = (
	request: RequestParameters,
	cacheConfig: CacheConfig = {},
): string => {
	if (shouldUseSSRBatchEndpoint()) {
		// SSR default: use /slow without PQ path (individual request endpoint)
		return `${AGG_SLOW_URI}?operation=${request.name}`;
	}

	// Client-side or SSR with PQ experiment enabled: use PQ endpoint
	let endpoint = AGG_BASE_URI;
	if (cacheConfig.metadata?.META_SLOW_ENDPOINT) {
		endpoint = AGG_SLOW_URI;
	}
	if (request.id != null) {
		endpoint = `${endpoint}/pq/${request.id}`;
	}

	return `${endpoint}?operation=${request.name}`;
};

/**
 * Returns the batch AGG endpoint for multiple requests during SSR.
 * Appends `/batch` to the slow endpoint as required by AGG for batched operations.
 * Should only be called when batching multiple requests together.
 */
export const getBatchEndpoint = (operationNames: string[]): string => {
	return `${AGG_SLOW_URI}/batch?operation=${operationNames.join(',')}`;
};

/**
 * Returns the request body for a given GraphQL request.
 *
 * During SSR (default, without the persisted query experiment):
 * - Includes the full query text in the body (required for non-PQ batch endpoint)
 *
 * During SSR with the `confluence_ssr_relay_persisted_query_endpoint` experiment enabled,
 * or on the client side:
 * - Sends only operationName and variables (PQ endpoint resolves the query by ID)
 */
export const getRequestBody = (request: RequestParameters, variables: Variables): string => {
	const requestBody: {
		operationName: string;
		variables: Variables;
		query?: string | null | undefined;
	} = {
		operationName: request.name,
		variables,
	};

	if (shouldUseSSRBatchEndpoint() && request.text) {
		// SSR default (batch/slow endpoint): include full query text since we're not using PQ
		requestBody.query = request.text;
	}

	return JSON.stringify(requestBody);
};
